import {
  Box,
  Tabs,
  Tab,
  CardMedia,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import { useLazyGetProductByIdQuery } from "../../service/product";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PRODUCT_STATUS } from "../../constants/enums";
import {
  useLazyProductReportsGraphQuery,
  useLazyProductViewsGraphQuery,
} from "../../service/revenueAnalytics";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
}
const activity = [
  {
    id: 1,
    user: "admin",
    action: "Edited product details",
    when: "2025-10-01 10:22",
  },
  {
    id: 2,
    user: "qa_user",
    action: "Uploaded new images",
    when: "2025-09-30 16:05",
  },
  {
    id: 3,
    user: "admin",
    action: "Updated pricing",
    when: "2025-09-29 09:47",
  },
];

const ProductDetail = () => {
  const downloadChartAsSVG = (
    chartRef: React.RefObject<HTMLDivElement>,
    filename: string
  ) => {
    if (!chartRef.current) return;

    const svgElement = chartRef.current.querySelector("svg");
    if (!svgElement) return;

    // Get the SVG source
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svgElement);

    // Add namespaces if not present
    if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    if (!source.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
      );
    }

    // Add XML declaration
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

    // Convert SVG source to URI data scheme
    const url =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);

    // Create download link
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };
  const [triggerGetById] = useLazyGetProductByIdQuery();
  const [triggerProductReportsGraph] = useLazyProductReportsGraphQuery();
  const [triggerProductViewsGraph] = useLazyProductViewsGraphQuery();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [type, setType] = useState("daily");
  const [reportsData, setReportsData] = useState<any>(null);
  const [viewsData, setViewsData] = useState<any>(null);
  const viewsChartRef = useRef<HTMLDivElement>(null);
  const reportsChartRef = useRef<HTMLDivElement>(null);

  const getProductReportsGraph = async (type: string) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await triggerProductReportsGraph({
        id: id as any,
        type,
      }).unwrap();
      const ok =
        res?.code === 200 ||
        (res as any)?.statusCode === 200 ||
        (res as any)?.status === 200;
      if (ok) {
        setReportsData((res as any)?.data || null);
      } else {
        setError((res as any)?.message || "Failed to fetch product reports");
      }
    } catch (e: any) {
      setError(
        e?.data?.message || e?.message || "Failed to fetch product reports"
      );
    } finally {
      setLoading(false);
    }
  };

  const getProductViewsGraph = async (type: string) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await triggerProductViewsGraph({
        id: id as any,
        type,
      }).unwrap();
      const ok =
        res?.code === 200 ||
        (res as any)?.statusCode === 200 ||
        (res as any)?.status === 200;
      if (ok) {
        setViewsData((res as any)?.data || null);
      } else {
        setError((res as any)?.message || "Failed to fetch product views");
      }
    } catch (e: any) {
      setError(
        e?.data?.message || e?.message || "Failed to fetch product views"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        setData(null);
        const res = await triggerGetById(id as any).unwrap();
        const ok =
          res?.code === 200 ||
          (res as any)?.statusCode === 200 ||
          (res as any)?.status === 200;
        if (ok) {
          setData((res as any)?.data || null);
        } else {
          setError((res as any)?.message || "Failed to fetch product details");
        }
      } catch (e: any) {
        setError(
          e?.data?.message || e?.message || "Failed to fetch product details"
        );
      } finally {
        setLoading(false);
      }
    };
    load();
    getProductReportsGraph(type);
    getProductViewsGraph(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <div className="main_title">
        <h1>Product Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-products")}>
            Manage Product Listing
          </button>{" "}
          - Product Detail
        </p>
      </div>

      {error && (
        <div className="cards" style={{ padding: 16 }}>
          <p style={{ color: "#d32f2f" }}>{error}</p>
          <Button
            className="btn btn_primary"
            onClick={() => {
              if (id) {
                setError(null);
                setLoading(true);
                triggerGetById(id as any)
                  .unwrap()
                  .then((res) => {
                    const ok =
                      res?.code === 200 ||
                      (res as any)?.statusCode === 200 ||
                      (res as any)?.status === 200;
                    if (ok) {
                      setData((res as any)?.data || null);
                      setError(null);
                    } else {
                      setError(
                        (res as any)?.message ||
                          "Failed to fetch product details"
                      );
                    }
                  })
                  .catch((e: any) =>
                    setError(
                      e?.data?.message ||
                        e?.message ||
                        "Failed to fetch product details"
                    )
                  )
                  .finally(() => setLoading(false));
              }
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {loading && (
        <div className="cards" style={{ padding: 16 }}>
          <p>Loading...</p>
        </div>
      )}

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Product Info" {...a11yProps(0)} />
              <Tab label="Monitor Analytics" {...a11yProps(1)} />
              <Tab label="User Activity" {...a11yProps(2)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards view_page">
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="gap_p">
            <div className="vew_img v2 no_radius w_100">
              <h6>Product Images/Videos</h6>
              <figure>
                {Array.isArray(data?.images) && data.images[0] ? (
                  <CardMedia
                    component="img"
                    image={data.images[0]}
                    alt="Product"
                  />
                ) : (
                  <img alt="placeholder" src="/static/images/placeholder.png" />
                )}
              </figure>
            </div>
            <div className="w_33">
              <h6>Product Title</h6>
              <p>{data?.name || data?.title || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Price</h6>
              <p>{data?.price != null ? String(data?.price) : "-"} GH₵</p>
            </div>
            <div className="w_33">
              <h6>Category</h6>
              <p>{data?.category?.name || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Sub Category</h6>
              <p>{data?.subCategory?.name || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Condition</h6>
              <p>{data?.condition || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Color</h6>
              <p>{data?.color || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Bulk Price</h6>
              <p>
                {data?.bulkPrice != null ? String(data?.bulkPrice) : "-"} GH₵
              </p>
            </div>
            <div className="w_66">
              <h6>Address</h6>
              <p>
                {data?.address
                  ? [
                      data.address.addressLine1,
                      data.address.addressLine2,
                      data.address.city,
                      data.address.state,
                      data.address.country,
                      data.address.pinCode,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : "-"}
              </p>
            </div>
            <div className="w_66">
              <h6>Booster Plan</h6>
              <p>{data?.boostPlan?.title || data?.boostPlan || "-"}</p>
            </div>
            <div className="w_100">
              <h6>Description</h6>
              <div
                dangerouslySetInnerHTML={{ __html: data?.description || "-" }}
              />
            </div>
            <div className="w_50">
              <h6>Contact Details</h6>
              <p>
                {(data?.contactDetails?.countryCode || "") +
                  " " +
                  (data?.contactDetails?.phone || "-")}
                <br />
                {data?.contactDetails?.email || "-"}
              </p>
            </div>
            <div className="w_50">
              <h6>Delivery Details</h6>
              <p>
                {data?.deliveryDetails
                  ? [
                      `Name : ${data.deliveryDetails.name || ""}`,
                      `Region : ${data.deliveryDetails.region || ""}`,
                      `Delivery Charges : ${
                        data.deliveryDetails.isDeliveryCharge ? "Yes" : "No"
                      }`,
                      `From : ${data.deliveryDetails.from || ""}`,
                      `To : ${data.deliveryDetails.to || ""}`,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : "-"}
              </p>
            </div>
            <div className="w_100">
              <h6>All Images</h6>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(Array.isArray(data?.images) ? data.images : []).map(
                  (img: string, i: number) => (
                    <img
                      key={i}
                      src={img}
                      alt={`img-${i}`}
                      style={{
                        width: 100,
                        height: 80,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  )
                )}
                {!Array.isArray(data?.images) || data.images.length === 0 ? (
                  <img
                    src="/static/images/placeholder.png"
                    alt="placeholder"
                    style={{
                      width: 100,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                ) : null}
              </div>
            </div>
            <div className="w_33">
              <h6>Status</h6>
              <p>
                {data?.isReport
                  ? "Reported"
                  : data?.status === PRODUCT_STATUS.APPROVED
                  ? "Approved"
                  : data?.status === PRODUCT_STATUS.REJECTED
                  ? "Rejected"
                  : "Pending"}
              </p>
            </div>
            {/* <div className="w_100">
              <h6>Features</h6>
              <div className="gap_p">
                {product.features.map((f, i) => (
                  <div key={i}>
                    <Chip
                      key={f.title}
                      avatar={
                        f.logo ? (
                          <Avatar src={f.logo} />
                        ) : (
                          <Avatar>{f.title.charAt(0).toUpperCase()}</Avatar>
                        )
                      }
                      label={`${f.title}${
                        f.description ? `: ${f.description}` : ""}
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </CustomTabPanel>

        <CustomTabPanel value={valueTabs} index={1}>
          <div className="grid_sc gap_m">
            <div className="cards table_card">
              <div className="cards_header">
                <div className="left_s">
                  <h2>Views</h2>
                </div>
                <div className="right_s">
                  <p>
                    <span onClick={() => getProductViewsGraph("daily")}>
                      A day
                    </span>
                    <span onClick={() => getProductViewsGraph("weekly")}>
                      A week
                    </span>
                    <span onClick={() => getProductViewsGraph("monthly")}>
                      A month
                    </span>
                    {/* <span>A year</span> */}
                  </p>
                  <Button
                    className="btn btn_black icon_btn"
                    onClick={() =>
                      downloadChartAsSVG(viewsChartRef, "views_chart")
                    }
                    title="Download as SVG"
                  >
                    <FileDownloadIcon />
                  </Button>
                </div>
              </div>
              <div ref={viewsChartRef}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={viewsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#38B6FF" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="cards table_card">
              <div className="cards_header">
                <div className="left_s">
                  <h2>Reports & Complaints</h2>
                </div>
                <div className="right_s">
                  <p>
                    <span onClick={() => getProductReportsGraph("daily")}>
                      A day
                    </span>
                    <span onClick={() => getProductReportsGraph("weekly")}>
                      A week
                    </span>
                    <span onClick={() => getProductReportsGraph("monthly")}>
                      A month
                    </span>
                    {/* <span>A year</span> */}
                  </p>
                  <Button
                    className="btn btn_black icon_btn"
                    onClick={() =>
                      downloadChartAsSVG(reportsChartRef, "reports_chart")
                    }
                    title="Download as SVG"
                  >
                    <FileDownloadIcon />
                  </Button>
                </div>
              </div>
              <div ref={reportsChartRef}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#38B6FF" />
                    {/* <Bar dataKey="complaints" fill="#d32f2f" /> */}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </CustomTabPanel>

        <CustomTabPanel value={valueTabs} index={2}>
          <div className="gap_p">
            <div className="w_100">
              <h6>User Activity</h6>
              <TableContainer className="table_container">
                <Table>
                  <TableHead>
                    <TableRow hover>
                      <TableCell>Id</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Date</TableCell>
                      {/* <TableCell>Action</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity?.map((row: any) => (
                      <TableRow hover key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>
                          <Box className="user_block">
                            <p>
                              <b>{row.user}</b>
                            </p>
                          </Box>
                        </TableCell>
                        <TableCell>{row.action}</TableCell>
                        <TableCell>{row.when}</TableCell>
                        {/* <TableCell>
                          <div className="table_actions v2">
                            <IconButton onClick={() => navigate("/manage-products/detail") }>
                              <img src="/static/images/eye_icon.svg" alt="" />
                            </IconButton>
                            <IconButton>
                              <img src="/static/images/trash2_icon.svg" alt="" />
                            </IconButton>
                          </div>
                        </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </CustomTabPanel>
      </div>
    </>
  );
};

export default ProductDetail;
